import { useParams } from "react-router-dom";
import ApiStatus from "../apiStatus";
import { useDeleteUserGroup, useFetchUserGroup, useUpdateUserGroup } from "../hooks/UserGroupHooks";
import ValidationSummary from "../ValidationSummary";
import UserGroupForm from "./UserGroupForm";
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from "react";
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from "../AuthContext";
import { UserGroupDTO } from "../types/userGroupDTO";

const UserGroupEdit = () => {
    const { t } = useTranslation();
    const { roles } = useContext(AuthContext);
    const { organisationId, userGroupId } = useParams<{ organisationId: string, userGroupId: string }>();
    if (!organisationId) throw Error("Need a organisation id");
    if (!userGroupId) throw Error("Need a user group id");

    const { data, status, isSuccess } = useFetchUserGroup(organisationId, userGroupId);
    const updateUserGroupMutation = useUpdateUserGroup(organisationId);
    const deleteUserGroupMutation = useDeleteUserGroup(organisationId);
    const [userGroupToDelete, setUserGroupToDelete] = useState<UserGroupDTO | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const canDelete = roles.includes("SysAdmin") || roles.includes("OptiviAdmin");

    const handleUserGroupDelete = (userGroup: UserGroupDTO) => {
        setUserGroupToDelete(userGroup);
        setShowDeleteModal(true);
    };

    const handleDelete = () => {
        if (!userGroupToDelete) return;

        deleteUserGroupMutation.mutate(userGroupToDelete, {
            onSuccess: () => {
                setShowDeleteModal(false);
                toast.success(t('userGroup.deleteSuccess'));
                history.back();
            },
            onError: () => {
                setShowDeleteModal(false);
                toast.error(t('userGroup.deleteError'));
            }
        });
    };

    if (!isSuccess) return <ApiStatus status={status} />;

    const userGroupWithLocalDates = {
        ...data,
        validFrom: new Date(data.validFrom + 'Z'),
        validTo: new Date(data.validTo + 'Z'),
        lastModified: new Date(data.lastModified + 'Z')
    }

    return (
        <>
            {updateUserGroupMutation.isError && (
                <ValidationSummary error={updateUserGroupMutation.error} />
            )}
            {updateUserGroupMutation.isPending ? (
                <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                    <ClipLoader size={50} color={"#123abc"} loading={true} />
                    <p>{t('userGroup.editWait')}</p>
                </div>
            ) : (
                <UserGroupForm
                    headerText={t('userGroup.edit')}
                    userGroup={userGroupWithLocalDates}
                    submitted={(ug) => {
                        updateUserGroupMutation.mutate(ug, {
                            onSuccess: () => {
                                toast.success(t('userGroup.editSuccess'));
                                history.back();
                            },
                            onError: () => {
                                toast.error(t('userGroup.editError'));
                            }
                        });
                    }}
                    deleted={canDelete ? handleUserGroupDelete : undefined}
                />
            )}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('common.confirmation')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('userGroup.deleteConfirmation')}</p>
                    {userGroupToDelete && (
                        <div><strong>{t('common.name')}:</strong> {userGroupToDelete.name}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={handleDelete} disabled={deleteUserGroupMutation.isPending}>
                        {t('buttons.delete')}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteUserGroupMutation.isPending}>
                        {t('buttons.cancel')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserGroupEdit;
